/**
 * GameWorld - Main 3D game container
 * 
 * Manages scene loading, transitions, and the game loop.
 * Renders the appropriate scene based on GameContext state.
 */

import { Suspense, useState, useCallback, useEffect, Component } from 'react';
import { Canvas } from '@react-three/fiber';
import { Physics } from '@react-three/rapier';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { useGame, TRAVEL_METHODS } from '../../context/GameContext';
import { getScene, getSpawnPoint } from '../../config/sceneData';
import PlayerController from './PlayerController';
import MobileControls from './MobileControls';
import ThornhavenExterior from './scenes/ThornhavenExterior';
import TavernInterior from './scenes/TavernInterior';
import TestScene from './scenes/TestScene';
import { ArrowLeft, Map, X, AlertTriangle } from 'lucide-react';

// Error boundary for 3D rendering errors
class GameErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error('3D Game Error:', error, errorInfo);
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-black flex items-center justify-center p-4">
          <div className="max-w-md text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl text-amber-100 font-bold mb-2">3D World Error</h2>
            <p className="text-amber-100/70 mb-4">
              The 3D environment failed to load. This may be due to WebGL compatibility issues.
            </p>
            <p className="text-xs text-red-400 mb-4 font-mono bg-black/50 p-2 rounded max-h-32 overflow-auto">
              {this.state.error?.message || 'Unknown error'}
            </p>
            <button
              onClick={this.props.onReturn}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500"
            >
              Return to Map
            </button>
          </div>
        </div>
      );
    }
    
    return this.props.children;
  }
}

// Scene component registry
const SCENE_COMPONENTS = {
  'thornhaven-exterior': ThornhavenExterior,
  'thornhaven-tavern-interior': TavernInterior,
  'test': TestScene,
};

// Loading screen
function LoadingScreen() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black z-50">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-amber-900/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-amber-400 text-lg">Loading world...</p>
      </div>
    </div>
  );
}

// Interaction prompt UI
function InteractionPrompt({ interaction }) {
  if (!interaction) return null;
  
  return (
    <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-40 hidden md:block">
      <div className="px-4 py-2 rounded-lg bg-black/70 border border-amber-500/50 backdrop-blur-sm">
        <p className="text-amber-100 text-center">
          <span className="text-amber-400 font-bold">[E]</span> {interaction.prompt}
        </p>
      </div>
    </div>
  );
}

// Game HUD
function GameHUD({ onOpenMap, onExit, locationName }) {
  return (
    <div className="absolute top-0 left-0 right-0 z-40 p-4 flex items-center justify-between pointer-events-none">
      {/* Left side - Exit and Map buttons */}
      <div className="flex items-center gap-2 pointer-events-auto">
        <button
          onClick={onExit}
          className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-red-900/30 hover:border-red-500/50 transition-all"
          title="Exit Game"
        >
          <X className="w-5 h-5 text-red-400" />
        </button>
        <button
          onClick={onOpenMap}
          className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
          title="Open Map"
        >
          <Map className="w-5 h-5 text-amber-100" />
        </button>
      </div>
      
      {/* Center - Location name */}
      <div className="px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30">
        <span className="text-amber-100 text-sm font-medium">{locationName}</span>
      </div>
      
      {/* Right side - placeholder for future UI */}
      <div className="w-20" />
    </div>
  );
}

// Desktop controls hint
function ControlsHint() {
  const [visible, setVisible] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 8000);
    return () => clearTimeout(timer);
  }, []);
  
  if (!visible) return null;
  
  return (
    <div className="absolute bottom-4 left-4 z-40 hidden md:block animate-fadeIn">
      <div className="p-3 rounded-lg bg-black/70 border border-amber-900/30 backdrop-blur-sm text-sm">
        <p className="text-amber-100 mb-1"><span className="text-amber-400">WASD</span> - Move</p>
        <p className="text-amber-100 mb-1"><span className="text-amber-400">SHIFT</span> - Run</p>
        <p className="text-amber-100 mb-1"><span className="text-amber-400">E</span> - Interact</p>
        <p className="text-amber-100"><span className="text-amber-400">Click</span> - Lock camera</p>
        <button 
          onClick={() => setVisible(false)}
          className="mt-2 text-xs text-amber-500 hover:text-amber-400"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// Transition overlay
function TransitionOverlay({ isTransitioning }) {
  if (!isTransitioning) return null;
  
  return (
    <div className="absolute inset-0 bg-black z-50 animate-pulse pointer-events-none" />
  );
}

export default function GameWorld() {
  const navigate = useNavigate();
  const { 
    playerState, 
    isGameActive, 
    exitGame, 
    enterLocation, 
    exitLocation 
  } = useGame();
  
  const [currentInteraction, setCurrentInteraction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const currentScene = getScene(playerState.sceneId);
  const SceneComponent = SCENE_COMPONENTS[playerState.sceneId];
  
  // Handle scene loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [playerState.sceneId]);
  
  // Handle interaction
  const handleInteraction = useCallback((interaction, execute = false) => {
    if (!execute) {
      setCurrentInteraction(interaction);
      return;
    }
    
    if (!interaction) return;
    
    const { action } = interaction;
    
    switch (action.type) {
      case 'enterScene':
        const targetScene = getScene(action.targetSceneId);
        const spawnPoint = getSpawnPoint(action.targetSceneId, action.spawnPoint);
        if (targetScene && spawnPoint) {
          enterLocation(
            targetScene.locationId || null,
            action.targetSceneId,
            spawnPoint
          );
        }
        break;
        
      case 'exitScene':
        const exitSpawnPoint = getSpawnPoint(action.targetSceneId, action.spawnPoint);
        if (exitSpawnPoint) {
          exitLocation(action.targetSceneId, exitSpawnPoint);
        }
        break;
        
      case 'showText':
        // TODO: Show text dialog
        console.log('Sign:', action.text);
        break;
        
      default:
        console.log('Unknown action:', action);
    }
  }, [enterLocation, exitLocation]);
  
  // Exit to map/travel UI
  const handleExit = useCallback(() => {
    exitGame();
    navigate('/world/midgard');
  }, [exitGame, navigate]);
  
  // Open map overlay (could show mini-map or navigate to map)
  const handleOpenMap = useCallback(() => {
    // For now, just navigate to the map
    exitGame();
    navigate('/world/midgard');
  }, [exitGame, navigate]);
  
  if (!SceneComponent) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Scene not found: {playerState.sceneId}</p>
          <button
            onClick={handleExit}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500"
          >
            Return to Map
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <GameErrorBoundary onReturn={handleExit}>
      <div className="fixed inset-0 bg-black" data-testid="game-world">
        {/* Loading overlay */}
        {isLoading && <LoadingScreen />}
        
        {/* Transition overlay */}
        <TransitionOverlay isTransitioning={playerState.isTransitioning} />
        
        {/* HUD */}
        <GameHUD 
          onOpenMap={handleOpenMap}
          onExit={handleExit}
          locationName={currentScene?.name || 'Unknown'}
        />
        
        {/* Desktop interaction prompt */}
        <InteractionPrompt interaction={currentInteraction} />
        
        {/* Desktop controls hint */}
        <ControlsHint />
        
        {/* Mobile controls */}
        <MobileControls 
          currentInteraction={currentInteraction}
          onInteract={handleInteraction}
        />
        
        {/* 3D Canvas */}
        <Canvas
          shadows
          camera={{ fov: 60, near: 0.1, far: 500, position: [0, 5, 15] }}
          gl={{ antialias: true, alpha: false }}
          onCreated={({ gl, scene }) => {
            // Set default background color
            scene.background = new THREE.Color('#87CEEB');
          }}
        >
          <Suspense fallback={null}>
            <Physics gravity={[0, -20, 0]} debug={false}>
              {/* Current scene */}
              <SceneComponent />
              
              {/* Player */}
              <PlayerController 
                sceneId={playerState.sceneId}
                onInteraction={handleInteraction}
              />
            </Physics>
          </Suspense>
        </Canvas>
      </div>
    </GameErrorBoundary>
  );
}
